// ════════════════════════════════════════════════════════════════════
// i18n catalog — AdminDailyLog page (namespace: dailylog.*)
//
// One entry per user-facing STATIC UI string in pages/AdminDailyLog.js.
// `en` values are copied VERBATIM from the page; `ar` values are accurate
// Modern Standard Arabic. The parent merges this into lib/i18n.js's catalog.
//
// NOT translated (left as live data / non-UI): platform names (from channel
// data), brand names (Netrev, Stripe, Pro), currency symbols & figures, CSV
// file column headers (they round-trip with import matching), dynamic
// interpolated values (amounts, row numbers, platform strings in errors).
// Numbers/symbols inside the Arabic are kept as-is.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── Validation / alert / error copy (split so dynamic values interpolate) ──
  "dailylog.err.dateGrossPlatformRequired": "Date, platform and gross required",
  "dailylog.err.kotRequired": "KOT number is required",
  "dailylog.err.kotDuplicate": "This KOT number is already logged for this platform on this date.",
  "dailylog.err.netAboveGrossPrefix": "Net (",
  "dailylog.err.netAboveGrossMid": ") can't be higher than Gross (",
  "dailylog.err.netAboveGrossSuffix": "). Net is sales after discount.",
  "dailylog.err.saveFailed": "Failed to save. Check connection.",
  "dailylog.err.accessEnded": "Your trial or subscription has ended — subscribe to keep adding entries.",
  "dailylog.err.cpcAllRequired": "Platform, both dates and amount are required.",
  "dailylog.err.endBeforeStart": "End date can't be before start date.",
  "dailylog.err.cpcDatesAmountRequired": "Both dates and amount are required.",
  "dailylog.alert.updateFailedPrefix": "Update failed: ",
  "dailylog.alert.updateFailedConn": "Update failed. Check connection.",
  "dailylog.alert.deleteFailedPrefix": "Delete failed: ",
  "dailylog.confirm.deleteCpc": "Delete this CPC entry? This cannot be undone.",
  "dailylog.confirm.deleteLog": "Delete this log entry? This cannot be undone.",
  "dailylog.alert.cannotIdentifyDelete": "Cannot identify entry to delete.",
  "dailylog.alert.cannotIdentifyEdit": "Cannot identify entry to edit.",
  "dailylog.alert.dateRequired": "Date is required.",
  "dailylog.alert.netAboveGross": "Net sales can't be greater than gross sales.",
  "dailylog.alert.pickBothDates": "Pick both start and end dates.",
  "dailylog.alert.startBeforeEnd": "Start date must be before end date.",
  "dailylog.alert.noDataToExport": "No data to export for this range. Try a different date range or add log entries first.",

  // ── Import error messages (split around interpolated row number / values) ──
  "dailylog.import.rowPrefix": "Row ",
  "dailylog.import.unknownPlatform": ": unknown platform \"",
  "dailylog.import.unknownPlatformBlank": "(blank)",
  "dailylog.import.unknownPlatformSuffix": "\"",
  "dailylog.import.badDate": ": bad/missing date (use DD/MM/YYYY)",
  "dailylog.import.missingGross": ": missing/invalid gross",
  "dailylog.import.netExceedsGrossA": ": net (",
  "dailylog.import.netExceedsGrossB": ") can't exceed gross (",
  "dailylog.import.netExceedsGrossC": ")",

  // ── Page header (SectionHdr) ──
  "dailylog.page.title": "Daily log",
  "dailylog.page.workspaceFallback": "Workspace",
  "dailylog.filter.allPlatforms": "All platforms",

  // ── Period P&L stat cards ──
  "dailylog.pnl.title": "P&L breakdown — period totals",
  "dailylog.pnl.infoTitle": "Net Receivable = your sales minus aggregator commission, card fees and CPC ad-spend, and the recoverable VAT on those fees. CPC uses your FULL committed spend for the period — including days with no sales entry — so totals can differ from adding up the visible rows. The VAT figure is charged on the total Net Receivable, not the sum of each row's VAT.",
  "dailylog.pnl.infoTopUpPrefix": "About ",
  "dailylog.pnl.infoTopUpSuffix": " of CPC was added for days with no sales entry.",
  // ── Daily-log totals-row info tooltips (click-to-toggle) ──
  "dailylog.totals.info.netRecv": "Net receivable figure is before subscription fees. Please refer to Insight+ for the subscription deduction and a closer calculation on net receivable per platform.",
  "dailylog.stat.info.netRecvCard": "This figure will deduct any future CPC and will provide a net receivable after the deduction.",
  "dailylog.totals.info.vat": "VAT payable — charged on the total Net Receivable (Net Receivable × VAT rate), not the sum of each row's VAT.",
  "dailylog.totals.info.trueProfit": "Please note this figure deducts any future-dated CPC. Any outstanding CPC costs you have spent will be deducted from your net receivable; however, according to your agreement the deduction may take place weekly or monthly. Simultaneously, the subscription will be deducted from your net receivable on a monthly basis.",
  "dailylog.stat.gross": "Gross",
  "dailylog.stat.netReceivable": "Net Receivable",
  "dailylog.stat.vat": "VAT payable",
  "dailylog.stat.cogs": "COGS",
  "dailylog.stat.trueProfit": "True Profit",

  // ── Daily log section header + controls ──
  "dailylog.section.title": "Daily log",
  "dailylog.records": "records",
  "dailylog.btn.export": "Export",
  "dailylog.btn.exportTitle": "Download the rows shown as a styled .xlsx",
  "dailylog.btn.addLogEntry": "+ Add log entry",

  // ── Add log entry form ──
  "dailylog.form.newEntry": "New log entry",
  "dailylog.btn.cancel": "Cancel",
  "dailylog.form.platform": "Platform",
  "dailylog.form.date": "Date",
  "dailylog.form.grossSales": "Gross sales",
  "dailylog.form.netSales": "Net sales",
  "dailylog.form.grossPlaceholder": "350.00",
  "dailylog.form.netPlaceholder": "after discounts",
  "dailylog.form.kot": "KOT number",
  "dailylog.form.kotPlaceholder": "e.g. 0933",
  "dailylog.btn.saveEntry": "Save entry",
  "dailylog.btn.saving": "Saving...",

  // ── Daily log table column headers ──
  "dailylog.col.channel": "Channel",
  "dailylog.col.date": "Date",
  "dailylog.col.kot": "KOT",
  "dailylog.col.gross": "Gross",
  "dailylog.col.netTicket": "Net Ticket",
  "dailylog.col.discount": "Discount",
  "dailylog.col.commission": "Commission",
  "dailylog.col.vatComm": "VAT/Comm",
  "dailylog.col.cardFees": "Card Fees",
  "dailylog.col.vatCard": "VAT/Card",
  "dailylog.col.netRecv": "Net Recv.",
  "dailylog.col.vat": "VAT payable",
  "dailylog.col.cogs": "COGS",
  "dailylog.col.trueProfit": "True Profit",

  // ── Row action buttons (inline edit) ──
  "dailylog.btn.savingEllipsis": "Saving…",
  "dailylog.btn.save": "Save",
  "dailylog.btn.editEntry": "Edit entry",
  "dailylog.btn.deleteEntry": "Delete entry",

  // ── Rows from the integration feed (migration 046, lib/dailyLogFeed.js) ──
  // v1 has one provider and a row carries none, so UrbanPiper is named in the
  // text: a proper noun, the same in both languages. dayLocked is the one
  // message for the add form, the editor, delete and the CSV import.
  "dailylog.feed.badge": "From UrbanPiper",
  "dailylog.feed.rowLocked": "This entry's sales come from UrbanPiper, so they can't be changed or deleted here. Its campaign fee can still be updated by CSV import.",
  "dailylog.feed.dayLocked": "This day comes from UrbanPiper, so its sales can't be typed, changed or deleted here.",

  // ── Table empty / no-match states ──
  "dailylog.empty.title": "No entries yet",
  "dailylog.empty.message": "Add a daily log entry or import a CSV to see your P&L here.",
  "dailylog.first.title": "Your first entry — see what you actually keep",
  "dailylog.first.body": "Log one day of sales from your platform (yesterday works well). Netrev applies that platform's commission, card fees and VAT and shows your real margin — no spreadsheet needed.",
  "dailylog.first.cta": "Log yesterday's sales",
  "dailylog.first.resultTitle": "That's your real margin",
  "dailylog.first.resultOn": "On",
  "dailylog.first.resultSales": "sales were",
  "dailylog.first.resultAfterDiscounts": "after discounts",
  "dailylog.first.resultTook": "took",
  "dailylog.first.resultFeesLabel": "in commission, card fees and VAT on fees",
  "dailylog.first.resultKept": "You kept",
  "dailylog.first.resultOfNet": "of net sales",
  "dailylog.first.resultHint": "Rates are pre-filled with typical platform commissions — adjust them any time in Configuration, and keep logging daily to see the trend.",
  "dailylog.first.resultHintNoRates": "This platform has no commission or card fee set yet, so nothing was deducted. Add your real rates in Configuration to see your true margin.",
  "dailylog.first.dismiss": "Got it",
  "dailylog.noFilterMatch": "No rows match the column filters.",
  "dailylog.totals": "Totals",

  // ── CPC / Campaign spend section ──
  "dailylog.cpc.title": "CPC / Campaign spend",
  "dailylog.cpc.subtitle": "Ad / campaign spend over a date range — split evenly across days and applied to your daily P&L.",
  "dailylog.cpc.addBtn": "+ Add CPC spend",
  "dailylog.cpc.newSpend": "New CPC spend",
  "dailylog.cpc.platform": "Platform",
  "dailylog.cpc.amount": "CPC amount",
  "dailylog.cpc.amountPlaceholder": "0",
  "dailylog.cpc.fromDate": "From date",
  "dailylog.cpc.toDate": "To date",

  // ── CPC table column headers + states ──
  "dailylog.cpc.col.channel": "Channel",
  "dailylog.cpc.col.from": "From",
  "dailylog.cpc.col.to": "To",
  "dailylog.cpc.col.spend": "CPC spend",
  "dailylog.cpc.col.vat": "VAT on CPC",
  "dailylog.cpc.empty.title": "No CPC spend yet",
  "dailylog.cpc.empty.message": "Add a CPC / campaign spend date range to factor ad costs into your P&L.",
  "dailylog.cpc.editTitle": "Edit CPC spend",
  "dailylog.cpc.deleteTitle": "Delete CPC spend",

  // ── Platform contribution (co-funded discounts) ──
  "dailylog.cofund.tab": "Platform contribution",
  "dailylog.cofund.title": "Platform contribution (co-funded discounts)",
  "dailylog.cofund.subtitle": "When a platform co-funds a discount you run, its share is added to what the customer paid to give your true net ticket, and commission and card fees are charged on that. The Net Ticket column still shows what the customer paid. Amounts here cover each entry's whole date range.",
  "dailylog.cofund.newEntry": "New platform contribution",
  "dailylog.cofund.platform": "Platform",
  "dailylog.cofund.discountPct": "Discount you ran (%)",
  "dailylog.cofund.cofundPct": "Platform co-funds (% of the discount)",
  "dailylog.cofund.discountPlaceholder": "e.g. 50",
  "dailylog.cofund.fromDate": "From date",
  "dailylog.cofund.toDate": "To date",
  "dailylog.cofund.hint.title": "Worked example for an order of",
  "dailylog.cofund.hint.customerPays": "customer pays",
  "dailylog.cofund.hint.platformFunds": "platform funds",
  "dailylog.cofund.hint.youFund": "you fund",
  "dailylog.cofund.hint.feesOn": "commission and fees charged on",
  "dailylog.cofund.platformScopedHint": "To add one for another platform, pick that platform (or All) in the platform filter above.",
  "dailylog.cofund.loadFailed": "Platform contributions couldn't be loaded, so these figures don't include them. Refresh the page to try again.",
  "dailylog.cofund.loadFailedTab": "Adding and editing platform contributions is paused until they load, so nothing gets entered twice. Refresh the page to try again.",
  "dailylog.cofund.col.channel": "Channel",
  "dailylog.cofund.col.from": "From",
  "dailylog.cofund.col.to": "To",
  "dailylog.cofund.col.discountPct": "Discount %",
  "dailylog.cofund.col.cofundPct": "Co-fund %",
  "dailylog.cofund.col.split": "Split of order value",
  "dailylog.cofund.col.amount": "Platform contrib.",
  "dailylog.cofund.split.platform": "Platform",
  "dailylog.cofund.split.you": "You",
  "dailylog.cofund.logCol": "Platform contrib.",
  "dailylog.cofund.empty.title": "No platform contributions yet",
  "dailylog.cofund.empty.message": "Add the discount you ran and the share the platform co-funds for a date range to see your true net ticket.",
  "dailylog.cofund.noChannels": "Add a platform in Configuration first.",
  "dailylog.cofund.editTitle": "Edit platform contribution",
  "dailylog.cofund.deleteTitle": "Delete platform contribution",
  "dailylog.cofund.confirmDelete": "Delete this platform contribution entry? This cannot be undone.",
  "dailylog.cofund.err.allRequired": "Platform, both dates, discount % and co-fund % are required.",
  "dailylog.cofund.err.badNumber": "Enter numbers for the discount % and co-fund %, e.g. 50 and 45.",
  "dailylog.cofund.err.badNumberDiscount": "Enter a number for the discount %, e.g. 50.",
  "dailylog.cofund.err.badNumberCofund": "Enter a number for the co-fund %, e.g. 45.",
  "dailylog.cofund.err.discountRange": "Discount % must be more than 0 and no more than 100.",
  "dailylog.cofund.err.cofundRangePrefix": "Co-fund % must be between ",
  "dailylog.cofund.err.cofundRangeMid": "% and ",
  "dailylog.cofund.err.cofundRangeSuffix": "%.",
  "dailylog.cofund.err.overlapPrefix": "These dates overlap an existing ",
  "dailylog.cofund.err.overlapMid": " entry (",
  "dailylog.cofund.err.overlapSuffix": "). Edit or delete that entry instead.",
  "dailylog.cofund.err.invalidValues": "Those values aren't allowed. Check the dates and percentages.",
  "dailylog.cofund.err.goneOrNoAccess": "This entry was not changed: it no longer exists or you no longer have access. The list has been refreshed.",
  "dailylog.cofund.err.fedDays": "An integration feeds this platform on some of these days (their orders carry the feed badge in the Daily Log), so a Platform contribution can't cover them: co-funding stays off on fed days. End the entry before the first fed day, or delete it.",
  "dailylog.cofund.pnlInfoPrefix": "Net Receivable includes ",
  "dailylog.cofund.pnlInfoSuffix": " of platform contribution (co-funded discounts), added to the net ticket before commission and card fees.",
  "dailylog.cofund.firstResultPrefix": "The platform co-funded ",
  "dailylog.cofund.firstResultSuffix": " of that discount.",
  "dailylog.cofund.firstResultOfTrueNet": "of your true net ticket,",

  // ── CSV import / export block ──
  "dailylog.csv.title": "Import / export daily log (CSV)",
  "dailylog.csv.subtitle": "Export your entries, edit in a spreadsheet, and re-import. Platform must match a platform on this workspace; dates as DD/MM/YYYY.",
  "dailylog.csv.noun": "entries",
  "dailylog.csv.proFeaturePrefix": "CSV import is a ",
  "dailylog.csv.proFeatureBold": "Pro feature",
  "dailylog.csv.proFeatureDot": ". ",
  "dailylog.csv.upgrade": "Upgrade to unlock →",
};

export const ar = {
  // ── Validation / alert / error copy ──
  "dailylog.err.dateGrossPlatformRequired": "التاريخ والمنصة والمبيعات الإجمالية مطلوبة",
  "dailylog.err.kotRequired": "رقم KOT مطلوب",
  "dailylog.err.kotDuplicate": "رقم KOT هذا مُسجّل بالفعل لهذه المنصة في هذا التاريخ.",
  "dailylog.err.netAboveGrossPrefix": "صافي المبيعات (",
  "dailylog.err.netAboveGrossMid": ") لا يمكن أن يكون أعلى من الإجمالي (",
  "dailylog.err.netAboveGrossSuffix": "). صافي المبيعات هو المبيعات بعد الخصم.",
  "dailylog.err.saveFailed": "تعذّر الحفظ. تحقّق من الاتصال.",
  "dailylog.err.accessEnded": "انتهت فترتك التجريبية أو اشتراكك — اشترك للاستمرار في إضافة الإدخالات.",
  "dailylog.err.cpcAllRequired": "المنصة وكلا التاريخين والمبلغ مطلوبة.",
  "dailylog.err.endBeforeStart": "لا يمكن أن يكون تاريخ النهاية قبل تاريخ البداية.",
  "dailylog.err.cpcDatesAmountRequired": "كلا التاريخين والمبلغ مطلوبة.",
  "dailylog.alert.updateFailedPrefix": "فشل التحديث: ",
  "dailylog.alert.updateFailedConn": "فشل التحديث. تحقّق من الاتصال.",
  "dailylog.alert.deleteFailedPrefix": "فشل الحذف: ",
  "dailylog.confirm.deleteCpc": "حذف هذا الإدخال لتكلفة النقرة؟ لا يمكن التراجع عن هذا.",
  "dailylog.confirm.deleteLog": "حذف هذا الإدخال من السجل؟ لا يمكن التراجع عن هذا.",
  "dailylog.alert.cannotIdentifyDelete": "تعذّر تحديد الإدخال المراد حذفه.",
  "dailylog.alert.cannotIdentifyEdit": "تعذّر تحديد الإدخال المراد تعديله.",
  "dailylog.alert.dateRequired": "التاريخ مطلوب.",
  "dailylog.alert.netAboveGross": "لا يمكن أن يكون صافي المبيعات أكبر من المبيعات الإجمالية.",
  "dailylog.alert.pickBothDates": "اختر تاريخي البداية والنهاية معًا.",
  "dailylog.alert.startBeforeEnd": "يجب أن يكون تاريخ البداية قبل تاريخ النهاية.",
  "dailylog.alert.noDataToExport": "لا توجد بيانات للتصدير في هذا النطاق. جرّب نطاقًا زمنيًا مختلفًا أو أضف إدخالات السجل أولًا.",

  // ── Import error messages ──
  "dailylog.import.rowPrefix": "الصف ",
  "dailylog.import.unknownPlatform": ": منصة غير معروفة \"",
  "dailylog.import.unknownPlatformBlank": "(فارغ)",
  "dailylog.import.unknownPlatformSuffix": "\"",
  "dailylog.import.badDate": ": تاريخ خاطئ أو مفقود (استخدم DD/MM/YYYY)",
  "dailylog.import.missingGross": ": إجمالي مفقود أو غير صالح",
  "dailylog.import.netExceedsGrossA": ": صافي المبيعات (",
  "dailylog.import.netExceedsGrossB": ") لا يمكن أن يتجاوز الإجمالي (",
  "dailylog.import.netExceedsGrossC": ")",

  // ── Page header ──
  "dailylog.page.title": "السجل اليومي",
  "dailylog.page.workspaceFallback": "مساحة العمل",
  "dailylog.filter.allPlatforms": "كل المنصات",

  // ── Period P&L stat cards ──
  "dailylog.pnl.title": "تفصيل الأرباح والخسائر — إجماليات الفترة",
  "dailylog.pnl.infoTitle": "صافي المستحقات = مبيعاتك بعد خصم عمولة المنصّة ورسوم البطاقة وإنفاق تكلفة النقرة، وضريبة القيمة المضافة القابلة للاسترداد على تلك الرسوم. وتُحتسب تكلفة النقرة على كامل إنفاقك الملتزَم به خلال الفترة — بما في ذلك الأيام التي لا تحتوي على إدخال مبيعات — لذا قد تختلف الإجماليات عن جمع الصفوف الظاهرة. وتُحتسب ضريبة القيمة المضافة على إجمالي صافي المستحقات، وليس على مجموع ضريبة كل صف.",
  "dailylog.pnl.infoTopUpPrefix": "أُضيف نحو ",
  "dailylog.pnl.infoTopUpSuffix": " من تكلفة النقرة عن الأيام التي لا تحتوي على إدخال مبيعات.",
  // ── تلميحات معلومات صف الإجماليات (تُفتح بالنقر) ──
  "dailylog.totals.info.netRecv": "صافي المستحقات قبل رسوم الاشتراك. يُرجى الرجوع إلى Insight+ للاطّلاع على خصم الاشتراك وعلى حساب أدق لصافي المستحقات لكل منصة.",
  "dailylog.stat.info.netRecvCard": "سيخصم هذا الرقم أي تكلفة نقرة مستقبلية، ويعرض صافي المستحقات بعد الخصم.",
  "dailylog.totals.info.vat": "ضريبة القيمة المضافة المستحقة — تُحتسب على إجمالي صافي المستحقات (صافي المستحقات × نسبة الضريبة)، وليس على مجموع ضريبة كل صف.",
  "dailylog.totals.info.trueProfit": "يُرجى ملاحظة أن هذا الرقم يخصم أي تكلفة نقرة مستقبلية التاريخ. أي تكاليف تكلفة نقرة مستحقة أنفقتها ستُخصم من صافي مستحقاتك؛ غير أنه وفقًا لاتفاقك قد يتم الخصم أسبوعيًا أو شهريًا. وفي الوقت نفسه، سيُخصم الاشتراك من صافي مستحقاتك على أساس شهري.",
  "dailylog.stat.gross": "الإجمالي",
  "dailylog.stat.netReceivable": "صافي المستحقات",
  "dailylog.stat.vat": "ضريبة القيمة المضافة المستحقة",
  "dailylog.stat.cogs": "تكلفة البضاعة المباعة",
  "dailylog.stat.trueProfit": "الربح الحقيقي",

  // ── Daily log section header + controls ──
  "dailylog.section.title": "السجل اليومي",
  "dailylog.records": "سجلات",
  "dailylog.btn.export": "تصدير",
  "dailylog.btn.exportTitle": "تنزيل الصفوف المعروضة كملف ‎.xlsx‎ منسّق",
  "dailylog.btn.addLogEntry": "+ إضافة إدخال للسجل",

  // ── Add log entry form ──
  "dailylog.form.newEntry": "إدخال جديد للسجل",
  "dailylog.btn.cancel": "إلغاء",
  "dailylog.form.platform": "المنصة",
  "dailylog.form.date": "التاريخ",
  "dailylog.form.grossSales": "المبيعات الإجمالية",
  "dailylog.form.netSales": "صافي المبيعات",
  "dailylog.form.grossPlaceholder": "350.00",
  "dailylog.form.netPlaceholder": "بعد الخصومات",
  "dailylog.form.kot": "رقم KOT",
  "dailylog.form.kotPlaceholder": "مثال: 0933",
  "dailylog.btn.saveEntry": "حفظ الإدخال",
  "dailylog.btn.saving": "جارٍ الحفظ...",

  // ── Daily log table column headers ──
  "dailylog.col.channel": "القناة",
  "dailylog.col.date": "التاريخ",
  "dailylog.col.kot": "KOT",
  "dailylog.col.gross": "الإجمالي",
  "dailylog.col.netTicket": "صافي الطلب",
  "dailylog.col.discount": "الخصم",
  "dailylog.col.commission": "العمولة",
  "dailylog.col.vatComm": "ضريبة/عمولة",
  "dailylog.col.cardFees": "رسوم البطاقة",
  "dailylog.col.vatCard": "ضريبة/بطاقة",
  "dailylog.col.netRecv": "صافي المستحقات",
  "dailylog.col.vat": "ضريبة القيمة المضافة المستحقة",
  "dailylog.col.cogs": "تكلفة البضاعة المباعة",
  "dailylog.col.trueProfit": "الربح الحقيقي",

  // ── Row action buttons (inline edit) ──
  "dailylog.btn.savingEllipsis": "جارٍ الحفظ…",
  "dailylog.btn.save": "حفظ",
  "dailylog.btn.editEntry": "تعديل الإدخال",
  "dailylog.btn.deleteEntry": "حذف الإدخال",

  // ── Rows from the integration feed ──
  "dailylog.feed.badge": "من UrbanPiper",
  "dailylog.feed.rowLocked": "مبيعات هذا الإدخال واردة من UrbanPiper، لذا لا يمكن تغييرها أو حذفها هنا. ويمكن تحديث رسوم الحملة عبر استيراد ملف CSV.",
  "dailylog.feed.dayLocked": "مبيعات هذا اليوم واردة من UrbanPiper، لذا لا يمكن إدخالها أو تغييرها أو حذفها هنا.",

  // ── Table empty / no-match states ──
  "dailylog.empty.title": "لا توجد إدخالات بعد",
  "dailylog.empty.message": "أضف إدخالًا للسجل اليومي أو استورد ملف CSV لعرض أرباحك وخسائرك هنا.",
  "dailylog.first.title": "إدخالك الأول — اعرف ما تحتفظ به فعليًا",
  "dailylog.first.body": "سجّل مبيعات يوم واحد من منصتك (يوم أمس مناسب). يطبّق Netrev عمولة تلك المنصة ورسوم البطاقات والضريبة ويعرض هامشك الحقيقي — دون جداول بيانات.",
  "dailylog.first.cta": "سجّل مبيعات الأمس",
  "dailylog.first.resultTitle": "هذا هو هامشك الحقيقي",
  "dailylog.first.resultOn": "في",
  "dailylog.first.resultSales": "بلغت المبيعات",
  "dailylog.first.resultAfterDiscounts": "بعد الخصومات",
  "dailylog.first.resultTook": "أخذت",
  "dailylog.first.resultFeesLabel": "عمولةً ورسوم بطاقات وضريبة على الرسوم",
  "dailylog.first.resultKept": "احتفظت بـ",
  "dailylog.first.resultOfNet": "من صافي المبيعات",
  "dailylog.first.resultHint": "النسب معبّأة مسبقًا بعمولات المنصات المعتادة — عدّلها في أي وقت من الإعدادات، وواصل التسجيل يوميًا لترى الاتجاه.",
  "dailylog.first.resultHintNoRates": "لم يتم ضبط عمولة أو رسوم بطاقات لهذه المنصة بعد، لذلك لم يُخصم شيء. أضف نسبك الحقيقية من الإعدادات لترى هامشك الفعلي.",
  "dailylog.first.dismiss": "فهمت",
  "dailylog.noFilterMatch": "لا توجد صفوف تطابق عوامل تصفية الأعمدة.",
  "dailylog.totals": "الإجماليات",

  // ── CPC / Campaign spend section ──
  "dailylog.cpc.title": "تكلفة النقرة / إنفاق الحملات",
  "dailylog.cpc.subtitle": "إنفاق الإعلانات / الحملات على مدى نطاق زمني — يُوزّع بالتساوي على الأيام ويُطبَّق على أرباحك وخسائرك اليومية.",
  "dailylog.cpc.addBtn": "+ إضافة إنفاق تكلفة النقرة",
  "dailylog.cpc.newSpend": "إنفاق جديد لتكلفة النقرة",
  "dailylog.cpc.platform": "المنصة",
  "dailylog.cpc.amount": "مبلغ تكلفة النقرة",
  "dailylog.cpc.amountPlaceholder": "0",
  "dailylog.cpc.fromDate": "من تاريخ",
  "dailylog.cpc.toDate": "إلى تاريخ",

  // ── CPC table column headers + states ──
  "dailylog.cpc.col.channel": "القناة",
  "dailylog.cpc.col.from": "من",
  "dailylog.cpc.col.to": "إلى",
  "dailylog.cpc.col.spend": "إنفاق تكلفة النقرة",
  "dailylog.cpc.col.vat": "ضريبة القيمة المضافة على تكلفة النقرة",
  "dailylog.cpc.empty.title": "لا يوجد إنفاق لتكلفة النقرة بعد",
  "dailylog.cpc.empty.message": "أضف نطاقًا زمنيًا لإنفاق تكلفة النقرة / الحملات لإدراج تكاليف الإعلانات في أرباحك وخسائرك.",
  "dailylog.cpc.editTitle": "تعديل إنفاق تكلفة النقرة",
  "dailylog.cpc.deleteTitle": "حذف إنفاق تكلفة النقرة",

  // ── Platform contribution (co-funded discounts) ──
  "dailylog.cofund.tab": "مساهمة المنصة",
  "dailylog.cofund.title": "مساهمة المنصة (خصومات بتمويل مشترك)",
  "dailylog.cofund.subtitle": "عندما تشارك المنصة في تمويل خصم تقدّمه، تُضاف حصتها إلى ما دفعه العميل ليكون ذلك صافي طلبك الحقيقي، وعليه تُحتسب العمولة ورسوم البطاقة. يبقى عمود صافي الطلب يعرض ما دفعه العميل. تغطي المبالغ هنا كامل النطاق الزمني لكل إدخال.",
  "dailylog.cofund.newEntry": "مساهمة منصة جديدة",
  "dailylog.cofund.platform": "المنصة",
  "dailylog.cofund.discountPct": "الخصم الذي قدّمته (%)",
  "dailylog.cofund.cofundPct": "تموّل المنصة (% من الخصم)",
  "dailylog.cofund.discountPlaceholder": "مثال: 50",
  "dailylog.cofund.fromDate": "من تاريخ",
  "dailylog.cofund.toDate": "إلى تاريخ",
  "dailylog.cofund.hint.title": "مثال عملي لطلب بقيمة",
  "dailylog.cofund.hint.customerPays": "يدفع العميل",
  "dailylog.cofund.hint.platformFunds": "تموّل المنصة",
  "dailylog.cofund.hint.youFund": "تموّل أنت",
  "dailylog.cofund.hint.feesOn": "تُحتسب العمولة والرسوم على",
  "dailylog.cofund.platformScopedHint": "لإضافة مساهمة لمنصة أخرى، اختر تلك المنصة (أو «الكل») من فلتر المنصات في الأعلى.",
  "dailylog.cofund.loadFailed": "تعذّر تحميل مساهمات المنصات، لذا لا تشملها هذه الأرقام. حدّث الصفحة للمحاولة مرة أخرى.",
  "dailylog.cofund.loadFailedTab": "تم إيقاف إضافة مساهمات المنصات وتعديلها مؤقتًا حتى يتم تحميلها، حتى لا يُدخل أي شيء مرتين. حدّث الصفحة للمحاولة مرة أخرى.",
  "dailylog.cofund.col.channel": "القناة",
  "dailylog.cofund.col.from": "من",
  "dailylog.cofund.col.to": "إلى",
  "dailylog.cofund.col.discountPct": "نسبة الخصم %",
  "dailylog.cofund.col.cofundPct": "نسبة التمويل المشترك %",
  "dailylog.cofund.col.split": "توزيع قيمة الطلب",
  "dailylog.cofund.col.amount": "مساهمة المنصة",
  "dailylog.cofund.split.platform": "المنصة",
  "dailylog.cofund.split.you": "أنت",
  "dailylog.cofund.logCol": "مساهمة المنصة",
  "dailylog.cofund.empty.title": "لا توجد مساهمات من المنصات بعد",
  "dailylog.cofund.empty.message": "أضف الخصم الذي قدّمته والحصة التي تموّلها المنصة لنطاق زمني، لترى صافي طلبك الحقيقي.",
  "dailylog.cofund.noChannels": "أضف منصة من الإعدادات أولًا.",
  "dailylog.cofund.editTitle": "تعديل مساهمة المنصة",
  "dailylog.cofund.deleteTitle": "حذف مساهمة المنصة",
  "dailylog.cofund.confirmDelete": "حذف إدخال مساهمة المنصة هذا؟ لا يمكن التراجع عن هذا.",
  "dailylog.cofund.err.allRequired": "المنصة وكلا التاريخين ونسبة الخصم ونسبة التمويل المشترك مطلوبة.",
  "dailylog.cofund.err.badNumber": "أدخل نسبة الخصم ونسبة التمويل المشترك كأرقام، مثل 50 و45.",
  "dailylog.cofund.err.badNumberDiscount": "أدخل رقمًا لنسبة الخصم، مثل 50.",
  "dailylog.cofund.err.badNumberCofund": "أدخل رقمًا لنسبة التمويل المشترك، مثل 45.",
  "dailylog.cofund.err.discountRange": "يجب أن تكون نسبة الخصم أكبر من 0 ولا تتجاوز 100.",
  "dailylog.cofund.err.cofundRangePrefix": "يجب أن تكون نسبة التمويل المشترك بين ",
  "dailylog.cofund.err.cofundRangeMid": "% و",
  "dailylog.cofund.err.cofundRangeSuffix": "%.",
  "dailylog.cofund.err.overlapPrefix": "تتداخل هذه التواريخ مع إدخال موجود لمنصة ",
  "dailylog.cofund.err.overlapMid": " (",
  "dailylog.cofund.err.overlapSuffix": "). عدّل ذلك الإدخال أو احذفه بدلًا من ذلك.",
  "dailylog.cofund.err.invalidValues": "هذه القيم غير مسموح بها. تحقّق من التواريخ والنسب.",
  "dailylog.cofund.err.goneOrNoAccess": "لم يتم تغيير هذا الإدخال: لم يعد موجودًا أو لم تعد لديك صلاحية الوصول. تم تحديث القائمة.",
  "dailylog.cofund.err.fedDays": "يغذّي تكامل هذه المنصة في بعض هذه الأيام (تحمل طلباتها شارة الربط في السجل اليومي)، لذا لا يمكن أن تغطيها مساهمة منصة: يبقى التمويل المشترك متوقفًا في الأيام المغذّاة. اجعل الإدخال ينتهي قبل أول يوم مغذّى، أو احذفه.",
  "dailylog.cofund.pnlInfoPrefix": "يشمل صافي المستحقات ",
  "dailylog.cofund.pnlInfoSuffix": " من مساهمة المنصة (خصومات بتمويل مشترك)، تُضاف إلى صافي الطلب قبل العمولة ورسوم البطاقة.",
  "dailylog.cofund.firstResultPrefix": "موّلت المنصة ",
  "dailylog.cofund.firstResultSuffix": " من ذلك الخصم.",
  "dailylog.cofund.firstResultOfTrueNet": "من صافي طلبك الحقيقي البالغ",

  // ── CSV import / export block ──
  "dailylog.csv.title": "استيراد / تصدير السجل اليومي (CSV)",
  "dailylog.csv.subtitle": "صدّر إدخالاتك، عدّلها في جدول بيانات، ثم أعد استيرادها. يجب أن تطابق المنصة منصة في مساحة العمل هذه؛ التواريخ بصيغة DD/MM/YYYY.",
  "dailylog.csv.noun": "إدخالات",
  "dailylog.csv.proFeaturePrefix": "استيراد CSV هو ",
  "dailylog.csv.proFeatureBold": "ميزة Pro",
  "dailylog.csv.proFeatureDot": ". ",
  "dailylog.csv.upgrade": "ترقَّ لإلغاء القفل ←",
};
